import { useRef } from "react";
import { Link } from "react-router-dom";
import { Button, Card, CircleHelp, Tags, Upload } from "../ui";
import { guideUrl } from "../guidlines/reportVideoGuide";
import UploadZone from "../ludDocuments/components/UploadZone/UploadZone";
import { formatFileSize } from "../ludDocuments/components/ReportFileSlot/ReportFileSlot";
import { count } from "./format";
import { ACCEPTED_FILES, APP_LOGOS, appLabel, sectionLabel, sectionsFor } from "./reportAnalysisModel";

const fileName = (part) => part.currentFile?.uploadedName || part.currentFile?.originalName || part.partLabel || "—";

function SavedPart({ part, replacement, text, lang, disabled, onReplace, onDownload, onRemove }) {
  const input = useRef(null);
  return (
    <li className={`report-analysis-part ${replacement ? "is-replaced" : ""}`}>
      <div className="report-analysis-part__main">
        <strong className="report-analysis-part__name">{fileName(part)}</strong>
        <span className="report-analysis-muted">
          {[text.version(part.fileVersion), part.currentFile?.size ? formatFileSize(part.currentFile.size) : null, part.currentFile?.uploadedAt ? new Date(part.currentFile.uploadedAt).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-GB") : null].filter(Boolean).join(" · ")}
        </span>
        {replacement ? (
          <span className="report-analysis-part__replacement">
            <Tags variant="warning">{text.willReplace(part.fileVersion + 1)}</Tags> {replacement.name}
            {replacement.problem && <Tags variant="danger">{text.review.problem}</Tags>}
          </span>
        ) : (
          <Tags variant="neutral">{text.keep}</Tags>
        )}
      </div>
      <div className="report-analysis-part__actions">
        <Button size="sm" appearance="soft" onClick={() => onDownload(part)}>{text.download}</Button>
        {replacement ? (
          <Button size="sm" appearance="soft" variant="danger" onClick={() => onRemove(replacement.id)} disabled={disabled}>{text.cancel}</Button>
        ) : (
          <>
            <Button size="sm" appearance="soft" variant="warning" icon={<Upload size={15} />} onClick={() => input.current?.click()} disabled={disabled}>{text.replace}</Button>
            <input
              ref={input}
              type="file"
              hidden
              accept={ACCEPTED_FILES}
              aria-label={`${text.replace} ${fileName(part)}`}
              onChange={(event) => {
                const [picked] = Array.from(event.target.files || []);
                event.target.value = "";
                if (picked) onReplace(part, picked);
              }}
            />
          </>
        )}
      </div>
    </li>
  );
}

// One application's card in step 2: its report sections for the mode, the saved parts the analysis
// must include, new parts and replacements, and how many files it has. File checks are explained in
// step 3 only; a card shows at most a compact "Problem" mark on a file it cannot send.
export default function ApplicationFiles({ app, mode, parts, uploads, blockers, text, lang, disabled, onAdd, onReplace, onRemove, onDownload }) {
  const sections = sectionsFor(app, mode);
  const ready = !blockers.some((blocker) => !blocker.warningOnly);
  const fileCount = parts.length + uploads.filter((upload) => upload.operation === "add").length;
  return (
    <div className="report-analysis-appcard" data-testid={`app-files-${app}`}>
    <Card className="report-analysis-card report-analysis-appfiles" hoverable={false}>
      <div className="report-analysis-appfiles__head">
        <img src={APP_LOGOS[app]} alt="" aria-hidden="true" />
        <div className="report-analysis-appfiles__title">
          <h4>{appLabel(app, lang)}</h4>
          {/* A new tab: picked files live only in this page's memory and would be lost by leaving it. */}
          <Link
            className="report-analysis-guide-link"
            to={guideUrl(app, mode)}
            target="_blank"
            rel="noopener"
            title={text.guideVideo[mode](appLabel(app, lang))}
            aria-label={text.guideVideo[mode](appLabel(app, lang))}
            data-testid={`guide-video-${app}`}
          >
            <CircleHelp size={18} aria-hidden="true" />
          </Link>
        </div>
        <span className="report-analysis-muted" data-testid={`file-count-${app}`}>{fileCount ? text.flow.filesCount(fileCount, count(fileCount, lang)) : text.flow.noFilesYet}</span>
        <Tags variant={ready ? "success" : "warning"}>{ready ? text.ready : text.notReady}</Tags>
      </div>
      {parts.some((part) => !part.legacy) && <p className="report-analysis-muted">{text.includeAll}</p>}
      <div className="report-analysis-sections">
        {sections.map((section) => {
          const saved = parts.filter((part) => !part.legacy && part.section === section.key);
          const added = uploads.filter((upload) => upload.section === section.key && upload.operation === "add");
          const canAdd = section.multi || (!saved.length && !added.length);
          return (
            <section key={section.key} className="report-analysis-section" aria-label={sectionLabel(app, section.key, lang)}>
              <div className="report-analysis-section__head">
                <h5>{sectionLabel(app, section.key, lang)}</h5>
                {!saved.length && !added.length && <span className="report-analysis-muted">{text.noFiles} · {text.optional}</span>}
              </div>
              {saved.length > 0 && (
                <ul className="report-analysis-parts" aria-label={text.existing}>
                  {saved.map((part) => (
                    <SavedPart
                      key={part.partKey}
                      part={part}
                      replacement={uploads.find((upload) => upload.operation === "replace" && upload.partKey === part.partKey)}
                      text={text}
                      lang={lang}
                      disabled={disabled}
                      onReplace={(target, file) => onReplace(app, section.key, target, file)}
                      onDownload={onDownload}
                      onRemove={onRemove}
                    />
                  ))}
                </ul>
              )}
              {added.length > 0 && (
                <ul className="report-analysis-parts">
                  {added.map((upload) => (
                    <li key={upload.id} className="report-analysis-part is-new">
                      <div className="report-analysis-part__main">
                        <strong className="report-analysis-part__name">{upload.name}</strong>
                        <span className="report-analysis-muted">{formatFileSize(upload.size)}</span>
                        <Tags variant="primary">{text.newPart}</Tags>
                        {upload.problem && <Tags variant="danger">{text.review.problem}</Tags>}
                      </div>
                      <div className="report-analysis-part__actions">
                        <Button size="sm" appearance="soft" variant="danger" onClick={() => onRemove(upload.id)} disabled={disabled}>{text.remove}</Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {canAdd && (
                <UploadZone
                  lang={lang}
                  label={section.multi ? text.addFiles : text.addFile}
                  icon={<Upload size={18} />}
                  multiple={section.multi}
                  accept={ACCEPTED_FILES}
                  disabled={disabled}
                  onFiles={(files) => onAdd(app, section.key, Array.from(files || []))}
                />
              )}
            </section>
          );
        })}
      </div>
    </Card>
    </div>
  );
}
